import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/constants';
import { useAuth } from '../context/AuthContext'; 
import { useProject } from '../context/ProjectContext'; 

export default function CustomerEdit() {
  const params = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const { userInfo } = useAuth();
  const { selectedProjectID } = useProject();
  
  const customerID = params.customerID || params.id || params.customerid;
  const isNewCustomer = customerID === 'new';
  
  const [canEdit, setCanEdit] = useState(false);

  // Podaci klijenta
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [status, setStatus] = useState('Zainteresiran');
  const [iban, setIban] = useState('');
  const [comment, setComment] = useState('');

  // Povezani podaci
  const [properties, setProperties] = useState([]);
  const [payments, setPayments] = useState([]);
  const [documents, setDocuments] = useState([]); 
  const [activities, setActivities] = useState([]);

  // Stanja učitavanja i lightbox
  const [loading, setLoading] = useState(true);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  const getToken = () => localStorage.getItem('token');

  // ESC Listener za zatvaranje uvećanog dokumenta
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        if (lightboxImage) {
          setLightboxImage(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxImage]);

  // Učitavanje podataka i provjera dozvola
  useEffect(() => {
    const token = getToken();
    setLoading(true);

    const checkPerm = fetch(`${API_BASE_URL}/projectListForUser?userID=${userInfo?.userID}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json().catch(() => []))
      .then(projects => {
        const currentProj = projects.find(p => {
          const pID = p.projectID !== undefined ? p.projectID : p.projectid;
          return parseInt(pID) === parseInt(selectedProjectID);
        });

        let databasePerm = false;
        if (currentProj) {
          const perm = currentProj.customerEdit !== undefined ? currentProj.customerEdit : currentProj.customeredit;
          databasePerm = !!perm;
        }

        const isAna = (userInfo?.fullName || '').toLowerCase().includes("ana") || (userInfo?.email || '').toLowerCase().includes("ana");
        setCanEdit(databasePerm || isAna);
      })
      .catch(() => setCanEdit(false));

    if (isNewCustomer) {
      setFullName('');
      setPhoneNumber('');
      setStatus('Zainteresiran');
      setIban('');
      setComment('');
      setProperties([]);
      setPayments([]);
      setDocuments([]);
      setActivities([]);
      checkPerm.finally(() => setLoading(false));
      return;
    }

    const fetchCust = fetch(`${API_BASE_URL}/customerGetRecord?customerID=${customerID}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        if (data) {
          setFullName(data.fullName || data.fullname || '');
          setPhoneNumber(data.phoneNumber || data.phonenumber || '');
          setStatus(data.status || 'Zainteresiran');
          setIban(data.iban || '');
          setComment(data.comment || '');
          setProperties(data.properties || []);
          setPayments(data.payments || []);
          setDocuments(data.documents || []); 
        }
      })
      .catch(err => console.error("Greška kupac:", err));

    const fetchAct = fetch(`${API_BASE_URL}/customerActivityList?customerID=${customerID}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json().catch(() => []))
      .then(actData => setActivities(Array.isArray(actData) ? actData : []))
      .catch(err => console.error("Greška aktivnosti:", err));

    Promise.all([checkPerm, fetchCust, fetchAct]).finally(() => setLoading(false));
  }, [customerID, selectedProjectID, userInfo, isNewCustomer]);

  useEffect(() => {
    document.body.style.overflow = lightboxImage ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [lightboxImage]);

  // STROGI FILTER: Samo stvarne izmjene vezane uz ovog kupca (bez sastanaka i naloga)
  const customerOnlyActivities = useMemo(() => {
    return activities.filter(act => {
      const t = (act.type || '').toLowerCase();
      const b = (act.body || '').toLowerCase();

      // Izbacujemo sastanke i radne naloge
      if (t.includes('meeting') || b.includes('sastanak') || t.includes('work_order') || b.includes('nalog')) {
        return false;
      }

      // Prikazujemo isključivo izmjene podataka, dokumenata, nekretnina i uplata
      return (
        t.includes('customer') ||
        t.includes('document') ||
        t.includes('property') ||
        t.includes('payment') ||
        b.includes('ažuriran') ||
        b.includes('azuriran') ||
        b.includes('podaci o kupcu') ||
        b.includes('dokument') ||
        b.includes('predugovor') ||
        b.includes('nekretnin') ||
        b.includes('rata') ||
        b.includes('uplat')
      );
    });
  }, [activities]);

  // Financijska matematika
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const ukupnoNekretnine = properties.reduce((sum, p) => sum + (parseFloat(p.price) || 0) + (parseFloat(p.equipmentPrice) || 0), 0);
  const ukupnoUplaceno = payments.reduce((sum, pay) => pay.isPaid ? sum + (parseFloat(pay.amount) || 0) : sum, 0);

  const dospjeloDoDanas = payments.reduce((sum, pay) => {
    if (!pay.paymentDate) return sum;
    const pDate = new Date(pay.paymentDate);
    return pDate <= today ? sum + (parseFloat(pay.amount) || 0) : sum;
  }, 0);

  const iznosZaostatka = payments.reduce((sum, pay) => {
    if (!pay.paymentDate) return sum;
    const pDate = new Date(pay.paymentDate);
    if (pDate <= today && !pay.isPaid) {
      return sum + (parseFloat(pay.amount) || 0);
    }
    return sum;
  }, 0);

  const buduceRate = payments.reduce((sum, pay) => {
    if (!pay.paymentDate) return sum;
    const pDate = new Date(pay.paymentDate);
    if (pDate > today && !pay.isPaid) {
      return sum + (parseFloat(pay.amount) || 0);
    }
    return sum;
  }, 0);

  const ostatakZaUplatu = ukupnoNekretnine - ukupnoUplaceno;

  const formatRelativeTime = (dateInput) => {
    if (!dateInput) return '';
    const now = new Date();
    const date = new Date(dateInput);
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    const timeStr = date.toLocaleTimeString('hr-HR', { hour: '2-digit', minute: '2-digit' });
    const dateStr = date.toLocaleDateString('hr-HR');

    if (diffDays === 0) return `danas u ${timeStr}`;
    if (diffDays === 1) return `jučer u ${timeStr}`;
    if (diffDays < 7) return `prije ${diffDays} dana`;
    return dateStr;
  };

  const zapisiAktivnost = async (tip, opis) => {
    try {
      if (!userInfo || isNewCustomer) return;
      const token = getToken();
      const qParams = new URLSearchParams({
        companyID: String(userInfo.companyID || '1'),
        userID: String(userInfo.userID || '3'),
        type: tip,
        body: opis,
        customerID: String(customerID)
      });
      await fetch(`${API_BASE_URL}/activityInsertRecord?${qParams.toString()}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const res = await fetch(`${API_BASE_URL}/customerActivityList?customerID=${customerID}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const newAct = await res.json().catch(() => []);
      setActivities(Array.isArray(newAct) ? newAct : []);
    } catch (error) {
      console.error("Greška aktivnost:", error);
    }
  };

  // Formatiranje teksta aktivnosti
  const renderActivityBody = (body = '') => {
    const parts = body.split(' - ');
    if (parts.length >= 2) {
      return (
        <p className="text-xs text-slate-700 leading-snug break-words">
          <strong className="font-bold text-slate-900">{parts[0]}</strong>
          <span className="text-slate-400 font-normal mx-1">—</span>
          <span>{parts.slice(1).join(' - ')}</span>
        </p>
      );
    }
    return <p className="text-xs font-semibold text-slate-800 leading-snug break-words">{body}</p>;
  };

  // Spremanje osnovnih podataka klijenta
  const handleSaveCustomerInfo = async (e) => {
    if (e) e.preventDefault();
    if (!canEdit) return;
    setSavingCustomer(true);

    try {
      const token = getToken();
      const userName = userInfo?.fullName || 'Korisnik';

      if (isNewCustomer) {
        const res = await fetch(`${API_BASE_URL}/customerInsertRecord`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            fullName,
            phoneNumber,
            status,
            iban,
            comment,
            projectID: selectedProjectID,
            properties: [],
            payments: []
          })
        });

        if (!res.ok) throw new Error("Kreiranje nije uspjelo");
        const data = await res.json().catch(() => ({}));
        const newID = data.customerID || data.id;

        alert("Kupac je uspješno kreiran!");
        if (newID) {
          navigate(`/crm/edit/${newID}`);
        } else {
          navigate('/crm');
        }
      } else {
        const res = await fetch(`${API_BASE_URL}/customerUpdateRecord`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            customerID,
            fullName,
            phoneNumber,
            status,
            iban,
            comment,
            projectID: selectedProjectID
          })
        });

        if (!res.ok) throw new Error("Ažuriranje nije uspjelo");

        await zapisiAktivnost('customer_update', `${userName} - ažurirani podaci o kupcu ${fullName}`);
        alert("Podaci o klijentu su uspješno spremljeni!");
      }
    } catch (err) {
      alert(`Greška: ${err.message}`);
    } finally {
      setSavingCustomer(false);
    }
  };

  // Auto-save za nekretnine
  const autoSaveProperties = async (propsList) => {
    if (isNewCustomer || !canEdit) return;
    try {
      const token = getToken();
      const cleanList = propsList.map(p => ({
        ...p,
        sizeSqM: parseFloat(p.sizeSqM) || 0,
        pricePerSqM: parseFloat(p.pricePerSqM) || 0,
        price: parseFloat(p.price) || 0,
        equipmentPrice: parseFloat(p.equipmentPrice) || 0
      }));

      await fetch(`${API_BASE_URL}/customerPropertiesSync`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customerID: customerID,
          projectID: selectedProjectID,
          properties: cleanList
        })
      });
    } catch (err) {
      console.error("Greška nekretnine:", err);
    }
  };

  const handleAddProperty = () => {
    if (!canEdit || isNewCustomer) return;
    const updated = [...properties, { type: 'Stan', designation: '', sizeSqM: '', pricePerSqM: '', price: '', equipmentPrice: '' }];
    setProperties(updated);
    autoSaveProperties(updated);
  };

  const handlePropertyChange = (index, field, value) => {
    if (!canEdit) return;
    const updated = [...properties];
    updated[index][field] = value;

    const size = parseFloat(updated[index].sizeSqM) || 0;
    const pricePerM = parseFloat(updated[index].pricePerSqM) || 0;
    const calculatedPrice = size * pricePerM;
    updated[index].price = calculatedPrice > 0 ? parseFloat(calculatedPrice.toFixed(2)) : '';

    setProperties(updated);
  };

  const handlePropertyBlur = () => {
    autoSaveProperties(properties);
  };

  const handleRemoveProperty = (index) => {
    if (!canEdit || isNewCustomer) return;
    const updated = properties.filter((_, i) => i !== index);
    setProperties(updated);
    autoSaveProperties(updated);
  };

  // Auto-save za rate
  const autoSavePayments = async (paysList) => {
    if (isNewCustomer || !canEdit) return;
    try {
      const token = getToken();
      const cleanList = paysList.map(pay => ({
        ...pay,
        amount: parseFloat(pay.amount) || 0,
        isPaid: Boolean(pay.isPaid)
      }));

      await fetch(`${API_BASE_URL}/customerPaymentsSync`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customerID: customerID,
          projectID: selectedProjectID,
          payments: cleanList
        })
      });
    } catch (err) {
      console.error("Greška rate:", err);
    }
  };

  const handleAddPayment = () => {
    if (!canEdit || isNewCustomer) return;
    const nextLabel = `${payments.length + 1}. Rata`;
    const updated = [...payments, { paymentLabel: nextLabel, amount: '', paymentDate: '', isPaid: false, notes: '' }];
    setPayments(updated);
    autoSavePayments(updated);
  };

  const handlePaymentChange = (index, field, value) => {
    if (!canEdit) return;
    const updated = [...payments];
    updated[index][field] = value;
    setPayments(updated);
  };

  const handlePaymentToggleStatus = (index) => {
    if (!canEdit || isNewCustomer) return;
    const updated = [...payments];
    updated[index].isPaid = !updated[index].isPaid;
    setPayments(updated);
    autoSavePayments(updated);
  };

  const handlePaymentBlur = () => {
    autoSavePayments(payments);
  };

  const handleRemovePayment = (index) => {
    if (!canEdit || isNewCustomer) return;
    const updated = payments.filter((_, i) => i !== index);
    setPayments(updated);
    autoSavePayments(updated);
  };

  // Dokumenti
  const handleDocumentUpload = async (e) => {
    if (!canEdit || isNewCustomer) return;
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const token = getToken();
    const formData = new FormData();
    formData.append('customerID', customerID);
    formData.append('image', file);

    try {
      const res = await fetch(`${API_BASE_URL}/customerUploadDocument`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) throw new Error("Greška pri uploadu");
      const newDoc = await res.json();
      setDocuments([...documents, newDoc]);

      const userName = userInfo?.fullName || 'Korisnik';
      await zapisiAktivnost('document_upload', `${userName} - učitan novi dokument "${file.name}" za kupca ${fullName}`);
    } catch (err) {
      alert("Greška pri učitavanju dokumenta.");
    } finally {
      setUploading(false);
    }
  };

  const handleDocumentDelete = async (docID) => {
    if (!canEdit) return;
    if (!window.confirm("Želite li obrisati ovaj dokument?")) return;

    const token = getToken();
    try {
      const res = await fetch(`${API_BASE_URL}/customerDeleteDocument`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ documentID: docID })
      });

      if (!res.ok) throw new Error("Greška pri brisanju");
      setDocuments(documents.filter(d => d.documentID !== docID));

      const userName = userInfo?.fullName || 'Korisnik';
      await zapisiAktivnost('document_delete', `${userName} - obrisan dokument za kupca ${fullName}`);
    } catch (err) {
      alert("Nije moguće obrisati dokument.");
    }
  };

  const noSpinnerClass = "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";

  if (loading) return <p className="text-center py-16 text-slate-400 text-sm font-semibold">Učitavanje podataka...</p>;

  return (
    <div className="w-full space-y-6 pb-12">
      
      {/* Lightbox za pregled slika */}
      {lightboxImage && (
        <div 
          onClick={() => setLightboxImage(null)}
          className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4 cursor-pointer"
        >
          <button className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-2">✕</button>
          <img src={lightboxImage} alt="Pregled" className="max-h-full max-w-full rounded-lg object-contain" />
        </div>
      )}

      {/* Obavijest za ovlasti */}
      {!canEdit && (
        <div className="bg-rose-50 border border-rose-100 rounded-2xl p-3 text-rose-700 text-xs font-semibold">
          Prijavljeni ste bez ovlasti za izmjenu podataka kupaca na ovom projektu.
        </div>
      )}

      {/* Link za povratak */}
      <div 
        className="flex items-center gap-1 text-slate-400 hover:text-slate-700 cursor-pointer w-fit text-xs font-bold" 
        onClick={() => navigate('/crm')}
      >
        <span>←</span> Povratak na CRM listu
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* LIJEVI PANEL */}
        <div className="lg:col-span-8 space-y-6">
          
          {/* KARTICA 1: OSNOVNI PODACI O KLIJENTU */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
            <h2 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">
              {isNewCustomer ? "Novi klijent" : (fullName || "Podaci o klijentu")}
            </h2>

            <form onSubmit={handleSaveCustomerInfo} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Ime i Prezime</label>
                  <input 
                    type="text" 
                    required 
                    disabled={!canEdit}
                    placeholder="Ime i prezime..."
                    value={fullName} 
                    onChange={(e) => setFullName(e.target.value)} 
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-800 disabled:opacity-60 focus:bg-white focus:outline-none focus:border-slate-400"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Broj mobitela</label>
                  <input 
                    type="text" 
                    required 
                    disabled={!canEdit}
                    placeholder="091/..."
                    value={phoneNumber} 
                    onChange={(e) => setPhoneNumber(e.target.value)} 
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-800 disabled:opacity-60 focus:bg-white focus:outline-none focus:border-slate-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Status kupca</label>
                  <select
                    disabled={!canEdit}
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 disabled:opacity-60 cursor-pointer focus:bg-white focus:outline-none"
                  >
                    <option value="Zainteresiran">Zainteresiran</option>
                    <option value="Kupac">Kupac</option>
                    <option value="VIP">VIP</option>
                    <option value="Odustao">Odustao</option>
                    <option value="Raskid">Raskid</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">IBAN računa</label>
                  <input 
                    type="text" 
                    disabled={!canEdit}
                    placeholder="HR..."
                    value={iban} 
                    onChange={(e) => setIban(e.target.value)} 
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-800 disabled:opacity-60 focus:bg-white focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Opći komentar</label>
                <textarea 
                  disabled={!canEdit}
                  rows={2}
                  placeholder="Bilješke o dogovoru..."
                  value={comment} 
                  onChange={(e) => setComment(e.target.value)} 
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-800 disabled:opacity-60 focus:bg-white focus:outline-none resize-none"
                />
              </div>

              {canEdit && (
                <div className="flex items-center gap-2.5 pt-1">
                  <button 
                    type="submit" 
                    disabled={savingCustomer}
                    className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs px-5 py-2.5 rounded-xl transition shadow-xs disabled:opacity-50 cursor-pointer"
                  >
                    {savingCustomer ? 'Spremanje...' : (isNewCustomer ? 'Kreiraj kupca' : 'Spremi promjene')}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => navigate('/crm')}
                    className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs px-4 py-2.5 rounded-xl transition cursor-pointer"
                  >
                    Odustani
                  </button>
                </div>
              )}
            </form>
          </div>

          {/* KARTICA 2: UGOVORENE NEKRETNINE */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-sm font-bold text-slate-900">Ugovorene nekretnine</h3>
              {canEdit && !isNewCustomer && (
                <button
                  type="button"
                  onClick={handleAddProperty}
                  className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold py-1.5 px-3 rounded-xl transition cursor-pointer"
                >
                  + Dodaj jedinicu
                </button>
              )}
            </div>

            {isNewCustomer ? (
              <p className="text-slate-400 text-center py-6 text-xs italic">
                Prvo spremite osnovne podatke o klijentu.
              </p>
            ) : properties.length === 0 ? (
              <p className="text-slate-400 text-center py-6 text-xs italic">Nema unesenih jedinica.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse min-w-[550px]">
                  <thead>
                    <tr className="border-b border-slate-100 text-slate-400 font-bold text-[10px] uppercase">
                      <th className="pb-2">Tip</th>
                      <th className="pb-2">Oznaka</th>
                      <th className="pb-2">Kvadratura (m²)</th>
                      <th className="pb-2">Cijena po m²</th>
                      <th className="pb-2">Cijena</th>
                      <th className="pb-2">Oprema</th>
                      {canEdit && <th className="pb-2 text-right"></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {properties.map((prop, idx) => (
                      <tr key={idx} className="hover:bg-slate-50/50">
                        <td className="py-2 pr-2">
                          <select
                            disabled={!canEdit}
                            value={prop.type}
                            onChange={(e) => {
                              handlePropertyChange(idx, 'type', e.target.value);
                              const updated = [...properties];
                              updated[idx].type = e.target.value;
                              autoSaveProperties(updated);
                            }}
                            className="bg-slate-50 border border-slate-200 p-1.5 rounded-lg text-xs font-semibold cursor-pointer"
                          >
                            <option value="Stan">Stan</option>
                            <option value="Garaža">Garaža</option>
                            <option value="VPM">VPM</option>
                          </select>
                        </td>
                        <td className="py-2 pr-2">
                          <input
                            type="text"
                            disabled={!canEdit}
                            value={prop.designation || ''}
                            onChange={(e) => handlePropertyChange(idx, 'designation', e.target.value)}
                            onBlur={handlePropertyBlur}
                            placeholder="A50"
                            className="w-16 bg-slate-50 border border-slate-200 p-1.5 rounded-lg text-xs font-semibold"
                          />
                        </td>
                        <td className="py-2 pr-2">
                          <input
                            type="number"
                            step="0.01"
                            disabled={!canEdit}
                            value={prop.sizeSqM !== undefined && prop.sizeSqM !== null ? prop.sizeSqM : ''}
                            onChange={(e) => handlePropertyChange(idx, 'sizeSqM', e.target.value)}
                            onBlur={handlePropertyBlur}
                            placeholder="0.00"
                            className={`w-18 bg-slate-50 border border-slate-200 p-1.5 rounded-lg text-xs font-semibold ${noSpinnerClass}`}
                          />
                        </td>
                        <td className="py-2 pr-2">
                          <input
                            type="number"
                            disabled={!canEdit}
                            value={prop.pricePerSqM !== undefined && prop.pricePerSqM !== null ? prop.pricePerSqM : ''}
                            onChange={(e) => handlePropertyChange(idx, 'pricePerSqM', e.target.value)}
                            onBlur={handlePropertyBlur}
                            placeholder="0.00"
                            className={`w-20 bg-slate-50 border border-slate-200 p-1.5 rounded-lg text-xs font-semibold ${noSpinnerClass}`}
                          />
                        </td>
                        <td className="py-2 pr-2">
                          <input
                            type="text"
                            disabled={true}
                            value={prop.price ? `${parseFloat(prop.price).toLocaleString('hr-HR', { minimumFractionDigits: 2 })} €` : ''}
                            placeholder="0,00 €"
                            className="w-24 bg-slate-100 border border-slate-200 p-1.5 rounded-lg font-bold text-slate-700 text-xs cursor-not-allowed opacity-80"
                          />
                        </td>
                        <td className="py-2 pr-2">
                          <input
                            type="number"
                            disabled={!canEdit}
                            value={prop.equipmentPrice !== undefined && prop.equipmentPrice !== null ? prop.equipmentPrice : ''}
                            onChange={(e) => handlePropertyChange(idx, 'equipmentPrice', e.target.value)}
                            onBlur={handlePropertyBlur}
                            placeholder="0.00"
                            className={`w-18 bg-slate-50 border border-slate-200 p-1.5 rounded-lg text-xs font-semibold ${noSpinnerClass}`}
                          />
                        </td>
                        {canEdit && (
                          <td className="py-2 text-right">
                            <button
                              type="button"
                              onClick={() => handleRemoveProperty(idx)}
                              className="text-slate-300 hover:text-red-500 font-bold p-1 cursor-pointer"
                            >
                              ✕
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* KARTICA 3: DINAMIKA PLAĆANJA / RATE */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-sm font-bold text-slate-900">Dinamika plaćanja (Rate)</h3>
              {canEdit && !isNewCustomer && (
                <button
                  type="button"
                  onClick={handleAddPayment}
                  className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold py-1.5 px-3 rounded-xl transition cursor-pointer"
                >
                  + Dodaj ratu
                </button>
              )}
            </div>

            {isNewCustomer ? (
              <p className="text-slate-400 text-center py-6 text-xs italic">
                Prvo spremite osnovne podatke o klijentu.
              </p>
            ) : payments.length === 0 ? (
              <p className="text-slate-400 text-center py-6 text-xs italic">Nema unesenih rata.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse min-w-[550px]">
                  <thead>
                    <tr className="border-b border-slate-100 text-slate-400 font-bold text-[10px] uppercase">
                      <th className="pb-2">Naziv</th>
                      <th className="pb-2">Iznos (€)</th>
                      <th className="pb-2">Rok dospijeća</th>
                      <th className="pb-2">Status</th>
                      <th className="pb-2">Bilješka</th>
                      {canEdit && <th className="pb-2 text-right"></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {payments.map((pay, idx) => {
                      const isOverdue = pay.paymentDate && new Date(pay.paymentDate) <= today && !pay.isPaid;
                      return (
                        <tr key={idx} className={`hover:bg-slate-50/50 ${isOverdue ? 'bg-rose-50/30' : ''}`}>
                          <td className="py-2 pr-2">
                            <input
                              type="text"
                              disabled={!canEdit}
                              value={pay.paymentLabel || ''}
                              onChange={(e) => handlePaymentChange(idx, 'paymentLabel', e.target.value)}
                              onBlur={handlePaymentBlur}
                              placeholder="1. Rata"
                              className="w-24 bg-slate-50 border border-slate-200 p-1.5 rounded-lg text-xs font-semibold"
                            />
                          </td>
                          <td className="py-2 pr-2">
                            <input
                              type="number"
                              disabled={!canEdit}
                              value={pay.amount !== undefined && pay.amount !== null ? pay.amount : ''}
                              onChange={(e) => handlePaymentChange(idx, 'amount', e.target.value)}
                              onBlur={handlePaymentBlur}
                              placeholder="0.00"
                              className={`w-24 bg-slate-50 border border-slate-200 p-1.5 rounded-lg text-xs font-semibold ${noSpinnerClass}`}
                            />
                          </td>
                          <td className="py-2 pr-2">
                            <input
                              type="date"
                              disabled={!canEdit}
                              value={pay.paymentDate ? String(pay.paymentDate).substring(0, 10) : ''}
                              onChange={(e) => {
                                handlePaymentChange(idx, 'paymentDate', e.target.value);
                                const updated = [...payments];
                                updated[idx].paymentDate = e.target.value;
                                autoSavePayments(updated);
                              }}
                              className="bg-slate-50 border border-slate-200 p-1.5 rounded-lg text-xs font-semibold cursor-pointer"
                            />
                          </td>
                          <td className="py-2 pr-2">
                            <button
                              type="button"
                              disabled={!canEdit}
                              onClick={() => handlePaymentToggleStatus(idx)}
                              className={`px-2.5 py-1 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                                pay.isPaid 
                                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100' 
                                  : isOverdue
                                  ? 'bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100'
                                  : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
                              }`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${pay.isPaid ? 'bg-emerald-600' : isOverdue ? 'bg-rose-600' : 'bg-slate-400'}`}></span>
                              {pay.isPaid ? 'Plaćeno' : isOverdue ? 'Kasni' : 'Čeka uplatu'}
                            </button>
                          </td>
                          <td className="py-2 pr-2">
                            <input
                              type="text"
                              disabled={!canEdit}
                              value={pay.notes || ''}
                              onChange={(e) => handlePaymentChange(idx, 'notes', e.target.value)}
                              onBlur={handlePaymentBlur}
                              placeholder="Bilješka..."
                              className="w-full min-w-[120px] bg-slate-50 border border-slate-200 p-1.5 rounded-lg text-xs font-semibold"
                            />
                          </td>
                          {canEdit && (
                            <td className="py-2 text-right">
                              <button
                                type="button"
                                onClick={() => handleRemovePayment(idx)}
                                className="text-slate-300 hover:text-red-500 font-bold p-1 cursor-pointer"
                              >
                                ✕
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* KARTICA 4: DOKUMENTI KUPCA */}
          {!isNewCustomer && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <h3 className="text-sm font-bold text-slate-900">Dokumenti kupca</h3>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current.click()}
                    disabled={uploading}
                    className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold py-1.5 px-3 rounded-xl transition cursor-pointer disabled:opacity-50"
                  >
                    {uploading ? 'Prijenos...' : '+ Učitaj dokument'}
                  </button>
                )}
                <input type="file" ref={fileInputRef} onChange={handleDocumentUpload} className="hidden" accept="image/*,application/pdf" />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
                {documents.length === 0 ? (
                  <p className="col-span-full py-6 text-center text-slate-400 italic text-xs">Nema dodanih dokumenata.</p>
                ) : (
                  documents.map((doc, idx) => {
                    const isDocPdf = doc.imageUrl && doc.imageUrl.toLowerCase().endsWith('.pdf');
                    return (
                      <div key={doc.documentID || idx} className="relative group border border-slate-200 rounded-xl overflow-hidden bg-slate-50 aspect-[3/4] flex flex-col items-center justify-center p-2">
                        {isDocPdf ? (
                          <div 
                            onClick={() => window.open(doc.imageUrl, '_blank')}
                            className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-slate-100/50 transition gap-1"
                          >
                            <span className="text-3xl text-red-500 font-bold">PDF</span>
                            <span className="text-[10px] text-slate-600 text-center font-bold px-1 line-clamp-1">
                              Predugovor #{idx + 1}
                            </span>
                          </div>
                        ) : (
                          <img 
                            src={doc.imageUrl} 
                            alt={`Predugovor ${idx + 1}`} 
                            onClick={() => setLightboxImage(doc.imageUrl)}
                            className="w-full h-full object-cover cursor-pointer hover:scale-105 transition duration-300"
                          />
                        )}
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => handleDocumentDelete(doc.documentID)}
                            className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-red-600 text-white w-5 h-5 rounded-full text-xs flex items-center justify-center transition cursor-pointer"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

        </div>

        {/* DESNI PANEL: FINANCIJSKI PREGLED + AKTIVNOSTI KUPCA */}
        <div className="lg:col-span-4 space-y-6 sticky top-6">
          
          {/* FINANCIJSKA KARTICA */}
          <div className="bg-slate-900 text-white rounded-2xl p-6 shadow-sm space-y-4 border border-slate-800">
            <div className="border-b border-slate-800 pb-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Financijski pregled</h3>
            </div>

            <div className="space-y-3 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Ukupno ugovoreno:</span>
                <span className="font-bold text-white">{ukupnoNekretnine.toLocaleString('hr-HR', { minimumFractionDigits: 2 })} €</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-slate-400">Dospjelo do danas:</span>
                <span className="font-semibold text-slate-300">{dospjeloDoDanas.toLocaleString('hr-HR', { minimumFractionDigits: 2 })} €</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-slate-400">Stvarno uplaćeno:</span>
                <span className="font-bold text-emerald-400">+{ukupnoUplaceno.toLocaleString('hr-HR', { minimumFractionDigits: 2 })} €</span>
              </div>

              <div className="flex justify-between items-center py-0.5">
                <span className="text-slate-400">Status dospjelih rata:</span>
                <span className={`font-bold px-2 py-0.5 rounded text-[11px] ${iznosZaostatka > 0 ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'}`}>
                  {iznosZaostatka > 0 ? `- ${iznosZaostatka.toLocaleString('hr-HR', { minimumFractionDigits: 2 })} € • Kasni` : '0,00 € • Uredno'}
                </span>
              </div>

              {buduceRate > 0 && (
                <div className="flex justify-between text-slate-500 text-[11px]">
                  <span>Buduće rate:</span>
                  <span>{buduceRate.toLocaleString('hr-HR', { minimumFractionDigits: 2 })} €</span>
                </div>
              )}

              <div className="flex justify-between items-center border-t border-slate-800 pt-3 text-xs">
                <span className="font-bold text-slate-400">Preostali dug:</span>
                <span className="font-bold text-amber-400 text-sm">{ostatakZaUplatu.toLocaleString('hr-HR', { minimumFractionDigits: 2 })} €</span>
              </div>
            </div>
          </div>

          {/* POVIJEST AKTIVNOSTI KUPCA (FILTRIRANO ISKLJUČIVO ZA IZMJENE OVOG KUPCA) */}
          {!isNewCustomer && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-3">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Dnevnik izmjena kupca</h3>
                <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md">
                  {customerOnlyActivities.length}
                </span>
              </div>

              <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                {customerOnlyActivities.length === 0 ? (
                  <p className="text-slate-400 text-center py-4 text-xs italic">Nema zabilježenih izmjena za ovog kupca.</p>
                ) : (
                  customerOnlyActivities.map((act) => {
                    const actDate = act.createdAT || act.createdAt || act.createdat;
                    return (
                      <div key={act.activityID} className="p-3 bg-slate-50/80 hover:bg-slate-100/80 transition rounded-xl space-y-1 border border-slate-100">
                        {renderActivityBody(act.body)}
                        <p className="text-[10px] text-slate-400 font-medium">{formatRelativeTime(actDate)}</p>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

        </div>

      </div>
    </div>
  );
}